import {bindActionCreators} from "redux"
import TemplateView from "../../../TemplateView"
import StatusHeadersPanel from "./StatusHeadersPanel"
import {loadOnScroll, reloadCounters, reloadKanban, toggleMinimizedCards, toggleProjectCollapsed, updateTaskStatusAndPosition} from "../actions"
import {
    doesIgnoreCollapse,
    getCollapsedProjectsIdsSet,
    getProjectsWithGroupedTasks,
    getUpdatingTaskIds,
    getWorkflowStatuses,
    isMinimizedCards,
    isNeedToReloadCounters,
    isNeedToReloadKanban,
    thereAreNoTasks,
    isSearchEnable,
} from "../selectors"
import {
    getRecentlyCreatedTaskIds,
    getTaskStatusFilter,
    isNeedToShowSelection,
    isSingleProjectSelected,
    isSingleCustomerSelected,
    isBulkOperationsPanelShown,
    getLastCreatedTaskIds,
    getSingleCPSelection,
    getEditableTaskIds,
} from '../../common/selectors'
import {getCustomers} from '../../../entities/selectors'
import {removeLastCreatedTaskIdsAction} from '../../common/actions'
import {getWorkflowStatuses as getWorkflowStatusesMap, getWorkflowStatuses as getAllWorkflowStatuses} from '../../../entities/selectors'
import {getOpenTaskId} from "../../../panels/task/editPanel/selectors"
import {connect} from "../../../redux/utils"
import KanbanContent from "./KanbanContent"
import AutoScrollableWrapper from "./AutoScrollableWrapper"
import {closeAnyTaskPanel, openEditTaskPanel} from '../../../panels/task/editPanel/actions'
import {isESTEnabled} from '../../../features/selectors'
import TasksNotFoundMessage from '../../components/TasksNotFoundMessage'
import {changeTaskSelected} from '../../taskSelection/actions'
import {getSelectedTaskIds} from '../../taskSelection/selectors'
import LocalSettingsUpdater from './LocalSettingsUpdater'

export class KanbanViewContainer extends TemplateView {
    constructor({
        onTaskStatusAndPositionChanged = (taskId, newStatusId) => {},
        onAllSelectedChanged = allSelected => {},
        onMinimizedCardsChanged = minimizedCards => {},
        onProjectHeaderClick = projectId => {},
        onScrollToEnd = () => {},
        onTaskClick = taskId => {},
        closeAnyTaskPanel = () => {},
        onSelectTaskHandler = () => {},
        reloadKanban = () => {},
        reloadCounters = () => {},
        clearLastCreatedTaskIds = () => {},
    }) {
        super()

        this.state = {
            statuses: [],
            projectsWithGroupedTasks: [],
            customers: [],
            showSingleProject: false,
            showSingleCustomer: false,
            taskStatusFilter: {
                allSelected: true,
                statusTypes: [],
                statusIds: []
            },
            minimizedCards: false,
            taskIdWithDetailsOpen: null,
            selectedTaskIds: {},
            collapsedProjectIds: {},
            lastCreatedTaskIds: {},
            recentlyCreatedTaskIds: {},
            updatingTaskIds: [],
            workflowStatusesMap: {},
            isNeedToReload: false,
            isNeedToReloadCounters: false,
            showSelection: false,
            bulkOperationsPanelShown: false,
            showEstimate: false,
            thereAreNoTasks: false,
            isSearchEnable: false,
            ignoreProjectCollapse: false,
            singleCPSelection: {},
            manageableTaskIds: {},
        }

        this.callbacks = {
            onTaskStatusAndPositionChanged,
            onScrollToEnd,
            onProjectHeaderClick,
            onTaskClick,
            closeAnyTaskPanel,
            onSelectTaskHandler,
            onMinimizedCardsChanged,
            reloadKanban,
            reloadCounters,
            clearLastCreatedTaskIds,
        }

        this.onStatePropertyBecameTrue('isNeedToReload', () => this.callbacks.reloadKanban())

        this.onStatePropertyBecameTrue('isNeedToReloadCounters', () => this.callbacks.reloadCounters())

        this.onStateChanged(state => {
            this.statusHeadersPanel.setState({
                statuses: state.statuses,
                taskStatusFilter: state.taskStatusFilter
            })

            this.kanbanContent.setState({
                statuses: state.statuses,
                projectsWithGroupedTasks: state.projectsWithGroupedTasks,
                collapsedProjectIds: state.collapsedProjectIds,
                customers: state.customers,
                showSingleProject: state.showSingleProject,
                showSingleCustomer: state.showSingleCustomer,
                selectedTaskIds: state.selectedTaskIds,
                taskStatusFilter: state.taskStatusFilter,
                taskIdWithDetailsOpen: state.taskIdWithDetailsOpen,
                minimizedCards: state.minimizedCards,
                lastCreatedTaskIds: state.lastCreatedTaskIds,
                recentlyCreatedTaskIds: state.recentlyCreatedTaskIds,
                updatingTaskIds: state.updatingTaskIds,
                showSelection: state.showSelection,
                showEstimate: state.showEstimate,
                ignoreProjectCollapse: state.ignoreProjectCollapse,
                manageableTaskIds: state.manageableTaskIds,
            })

            this.kanbanContentVerticalScrollWrapper.refreshScroll()
            this.kanbanContentHorizontalScrollWrapper.refreshScroll()
        })

        this.onStatePropertyChanged('bulkOperationsPanelShown', () => {
            this.kanbanContentVerticalScrollWrapper.refreshScroll()
        })

        this.onStatePropertyChanged('thereAreNoTasks', (thereAreNoTasks) => {
            this.tasksNotFoundMessage.toggle(thereAreNoTasks)
            this.kanbanContentVerticalScrollWrapper.toggle(!thereAreNoTasks)
        })

        this.onAnyStatePropertyChanged(['singleCPSelection', 'isSearchEnable'], ({singleCPSelection, isSearchEnable}) => {
            this.tasksNotFoundMessage.setState({
                showLinkToAllTasks: isSearchEnable && (!!singleCPSelection.customerId || !!singleCPSelection.projectId),
            })
        })
    }

    templateHtml() {
        return "<div class='kanbanViewContainer'>" +
               "  <div class='content kanbanVerticalScrollContainer'></div>" +
               "</div>"
    }

    afterRender() {
        this.statusHeadersPanel = new StatusHeadersPanel()

        this.kanbanContent = new KanbanContent({
            onProjectHeaderClick: projectId => this.callbacks.onProjectHeaderClick(projectId),
            onTaskClick: taskId => this.callbacks.onTaskClick(taskId),
            closeAnyTaskPanel: this.callbacks.closeAnyTaskPanel,
            onSelectTaskHandler: this.callbacks.onSelectTaskHandler,
            onTaskStatusAndPositionChanged: this.callbacks.onTaskStatusAndPositionChanged,
            onCardActiveSelectionDisappear: this.callbacks.clearLastCreatedTaskIds,
        })

        this.kanbanContentVerticalScrollWrapper = new AutoScrollableWrapper({
            components: [this.kanbanContent],
            additionalClasses: 'kanbanVerticalScrollableWrapper',
            autoScrollEnabled: true,
            scrollEvent: 'sortableList_itemDrag',
            eventDataRetriever: (event, mouseEvent) => mouseEvent,
            enableVerticalScroll: true,
            verticalScrollConfig: {
                probeType: 2
            },
            verticalScrollContainerClass: 'kanbanVerticalScrollContainer',
            onScrollToBottom: () => this.callbacks.onScrollToEnd(),
            showScrollToTopButton: true,
        })

        const kanbanHorizontalScrollClassContainer = 'kanbanHorizontalScrollableWrapper'
        this.kanbanContentHorizontalScrollWrapper = new AutoScrollableWrapper({
            components: [this.statusHeadersPanel, this.kanbanContentVerticalScrollWrapper],
            additionalClasses: kanbanHorizontalScrollClassContainer,
            autoScrollEnabled: true,
            scrollEvent: 'sortableList_itemDrag',
            eventDataRetriever: (event, mouseEvent) => mouseEvent,
            enableHorizontalScroll: true,
            horizontalScrollConfig: {
                probeType: 2,
                mouseWheel: false
            },
        }).init(this.find(".content"))

        this.tasksNotFoundMessage = new TasksNotFoundMessage().init(this.find(`.${kanbanHorizontalScrollClassContainer}`))

        // use if need to store local settings when state updating
        new LocalSettingsUpdater().init()
    }

    show() {
        super.show()
        this.kanbanContentHorizontalScrollWrapper.show()
    }
}

export default connect(
    state => ({
        statuses: getWorkflowStatuses(state),
        allWorkflowStatuses: getAllWorkflowStatuses(state),
        projectsWithGroupedTasks: getProjectsWithGroupedTasks(state),
        taskIdWithDetailsOpen: getOpenTaskId(state),
        selectedTaskIds: getSelectedTaskIds(state),
        taskStatusFilter: getTaskStatusFilter(state),
        lastCreatedTaskIds: getLastCreatedTaskIds(state),
        recentlyCreatedTaskIds: getRecentlyCreatedTaskIds(state),
        minimizedCards: isMinimizedCards(state),
        customers: getCustomers(state),
        showSingleProject: isSingleProjectSelected(state),
        showSingleCustomer: isSingleCustomerSelected(state),
        updatingTaskIds: getUpdatingTaskIds(state),
        workflowStatusesMap: getWorkflowStatusesMap(state),
        isNeedToReload: isNeedToReloadKanban(state),
        isNeedToReloadCounters: isNeedToReloadCounters(state),
        showSelection: isNeedToShowSelection(state),
        bulkOperationsPanelShown: isBulkOperationsPanelShown(state),
        showEstimate: isESTEnabled(state),
        collapsedProjectIds: getCollapsedProjectsIdsSet(state),
        thereAreNoTasks: thereAreNoTasks(state),
        isSearchEnable: isSearchEnable(state),
        ignoreProjectCollapse: doesIgnoreCollapse(state),
        singleCPSelection: getSingleCPSelection(state),
        manageableTaskIds: getEditableTaskIds(state),
    }),
    dispatch => ({
        onScrollToEnd: bindActionCreators(loadOnScroll, dispatch),
        onTaskClick: bindActionCreators(openEditTaskPanel, dispatch),
        closeAnyTaskPanel: bindActionCreators(closeAnyTaskPanel, dispatch),
        onSelectTaskHandler: bindActionCreators(changeTaskSelected, dispatch),
        onProjectHeaderClick: bindActionCreators(toggleProjectCollapsed, dispatch),
        onMinimizedCardsChanged: bindActionCreators(toggleMinimizedCards, dispatch),
        onTaskStatusAndPositionChanged: bindActionCreators(updateTaskStatusAndPosition, dispatch),
        reloadKanban: bindActionCreators(reloadKanban, dispatch),
        reloadCounters: bindActionCreators(reloadCounters, dispatch),
        clearLastCreatedTaskIds: bindActionCreators(removeLastCreatedTaskIdsAction, dispatch),
    })
)(KanbanViewContainer)



// WEBPACK FOOTER //
// ./web/js/taskManagement/kanban/components/KanbanViewContainer.js